import React from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Box, Button, Container, Paper, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/**
 * Return landing for the DocuSign embedded-signing ceremony. DocuSign redirects
 * here with an `event` query param after the institution finishes (or leaves)
 * signing. No authentication required.
 *
 * DocuSign return events: signing_complete, viewing_complete, decline, cancel,
 * session_timeout, ttl_expired, exception, fax_pending.
 */
const OUTCOMES = {
  signing_complete: {
    icon: "success",
    title: "Thank you — your agreement is signed",
    body: "VoxPont's authorized signatory will now countersign. Once that's done, you and VoxPont each receive the fully executed copy by email. You can close this page.",
  },
  viewing_complete: {
    icon: "success",
    title: "Thank you for reviewing",
    body: "VoxPont's authorized signatory will complete the agreement. You'll receive the fully executed copy by email.",
  },
  decline: {
    icon: "error",
    title: "You declined the agreement",
    body: "No agreement was signed. If this was a mistake, you can start a new request at any time.",
  },
  cancel: {
    icon: "info",
    title: "Signing not completed",
    body: "It looks like the signing session was closed before it was finished. If you closed it by mistake, you can start a new request, or contact us and we'll send you a fresh signing link.",
  },
  session_timeout: {
    icon: "info",
    title: "Your signing session timed out",
    body: "For your security, the signing session expired. Start a new request, or contact us and we'll send you a fresh signing link.",
  },
  ttl_expired: {
    icon: "info",
    title: "This signing link expired",
    body: "Signing links are short-lived for security. Start a new request, or contact us and we'll send you a fresh signing link.",
  },
  exception: {
    icon: "error",
    title: "Something went wrong during signing",
    body: "We hit an unexpected error. Please start a new request, or contact us so our team can help.",
  },
};

const DEFAULT_OUTCOME = {
  icon: "info",
  title: "Signing session ended",
  body: "If you didn't finish signing, you can start a new request, or contact us and we'll send you a fresh signing link.",
};

const ICONS = {
  success: <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />,
  error: <ErrorOutlineIcon color="error" sx={{ fontSize: 48 }} />,
  info: <InfoOutlinedIcon color="info" sx={{ fontSize: 48 }} />,
};

export default function VoxPontBaaSignedPage() {
  const [searchParams] = useSearchParams();
  const event = searchParams.get("event") || "";
  const outcome = OUTCOMES[event] || DEFAULT_OUTCOME;
  const isSuccess = outcome.icon === "success";

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        minHeight: "100vh",
        py: { xs: 4, md: 8 },
      }}
    >
      <Container maxWidth="sm">
        <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 } }}>
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            {ICONS[outcome.icon]}
            <Typography variant="h4" component="h1">
              {outcome.title}
            </Typography>
            <Typography color="text.secondary">{outcome.body}</Typography>
            {!isSuccess && (
              <Button
                component={RouterLink}
                to="/voxpont-baa-request"
                variant="contained"
              >
                Start a new request
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
